import { createContext, useContext, useReducer } from 'react';
import type { PaginacionInfo } from '@/services/api/api.types';
import { getDefaultDateRangeEsteAnio } from '@/shared/time/businessTime';

export interface Comprobante {
  id: string;
  serie?: string;
  correlativo?: string;
  documentNumber?: string;
  type?: string;
  issueDate?: string;
  dueDate?: string;
  customerName?: string;
  customerDocument?: string;
  seller?: string;
  paymentMethod?: string;
  currency?: string;
  exchangeRate?: number;
  total?: number;
  status?: string;
  email?: string;
  label?: string;
  tieneMovimientoStock?: boolean;
  tieneNotaSalidaActiva?: boolean;
  origenVenta?: string;

  [key: string]: any;
}

export interface ComprobantesFilters {
  search: string;
  estados: string[];
  fechaDesde: string;
  fechaHasta: string;
  tipoDocumento: string;
  vendedor: string;
  formaPago: string;
  totalMin: string;
  totalMax: string;
}

export const INITIAL_FILTERS: ComprobantesFilters = {
  search: '',
  estados: [],
  ...getDefaultDateRangeEsteAnio(),
  tipoDocumento: '',
  vendedor: '',
  formaPago: '',
  totalMin: '',
  totalMax: '',
};

const DEFAULT_PAGINACION: PaginacionInfo = {
  page: 1,
  pageSize: 50,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrevious: false,
};

export interface ComprobantesListState {
  comprobantes: Comprobante[];
  esCargando: boolean;
  error: string | null;
  filters: ComprobantesFilters;
  page: number;
  pageSize: number;
  paginacion: PaginacionInfo;
  lastFetchedAt: number | null;
}


export type ComprobantesListAction = { type: string; payload?: any };

export interface ComprobantesListContextValue {
  state: ComprobantesListState;
  dispatch: React.Dispatch<ComprobantesListAction>;
  comprobantes: Comprobante[];
  esCargando: boolean;
  error: string | null;
  paginacion: PaginacionInfo;
  lastFetchedAt: number | null;
  addComprobante: (c: any) => void;
  updateComprobante: (id: string, changes: any) => void;
  refreshComprobantes: () => Promise<void>;
  setFilters: (partial: Partial<ComprobantesFilters>) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
}


const ComprobantesListCtx = createContext<any>(undefined);

function comprobantesReducer(
  state: ComprobantesListState,
  action: ComprobantesListAction,
): ComprobantesListState {
  switch (action.type) {
    case 'SET_COMPROBANTES':
      return { ...state, comprobantes: action.payload ?? [], esCargando: false };
    case 'SET_PAGINACION':
      return { ...state, paginacion: action.payload };
    case 'ADD_COMPROBANTE':
      return { ...state, comprobantes: [action.payload, ...state.comprobantes] };
    case 'UPDATE_COMPROBANTE': {
      const updated = action.payload as Comprobante;
      return {
        ...state,
        comprobantes: state.comprobantes.map(c =>
          c.id === updated.id ? { ...c, ...updated } : c,
        ),
      };
    }
    case 'SET_LOADING':
      return { ...state, esCargando: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, esCargando: false };
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload }, page: 1 };
    case 'SET_PAGE':
      return { ...state, page: action.payload };
    case 'SET_PAGE_SIZE':
      return { ...state, pageSize: action.payload, page: 1 };
    case 'SET_LAST_FETCHED':
      return { ...state, lastFetchedAt: action.payload };
    default:
      return state;
  }
}

const initialState: ComprobantesListState = {
  comprobantes: [],
  esCargando: false,
  error: null,
  filters: INITIAL_FILTERS,
  page: 1,
  pageSize: 50,
  paginacion: DEFAULT_PAGINACION,
  lastFetchedAt: null,
};

export function useComprobanteContext(): ComprobantesListContextValue {
  const ctx = useContext(ComprobantesListCtx);

  const [state, dispatch] = useReducer(comprobantesReducer, initialState);

  if (ctx) return ctx;

  return {
    state,
    dispatch,
    comprobantes: state.comprobantes,
    esCargando: state.esCargando,
    error: state.error,
    paginacion: state.paginacion,
    lastFetchedAt: state.lastFetchedAt,
    addComprobante: (c) => dispatch({ type: 'ADD_COMPROBANTE', payload: c }),
    updateComprobante: (id, changes) => dispatch({ type: 'UPDATE_COMPROBANTE', payload: { id, ...changes } }),
    refreshComprobantes: async () => {},
    setFilters: (partial) => dispatch({ type: 'SET_FILTERS', payload: partial }),
    setPage: (page) => dispatch({ type: 'SET_PAGE', payload: page }),
    setPageSize: (size) => dispatch({ type: 'SET_PAGE_SIZE', payload: size }),
  };
}

export { ComprobantesListCtx };
